import { useDisclosure } from '@mantine/hooks';
import PasswordValidityModal from 'components/modals/password-validity-modal/PasswordValidityModal';
import { PasswordValidityModalProps } from 'components/modals/password-validity-modal/PasswordValidityModal';
import { createContext, useContext, useMemo, useState } from 'react';

type OpenPasswordValidityModalParams = Omit<PasswordValidityModalProps, 'opened' | 'onClose'>;
type OpenPasswordValidityModal = (params: OpenPasswordValidityModalParams) => void;

interface IPasswordValidityModalContext {
  openPasswordValidityModal: OpenPasswordValidityModal;
}

const PasswordValidityModalContext = createContext<IPasswordValidityModalContext>(undefined!);

/**
 * Provider for opening the password validity modal.
 */
export function PasswordValidityModalProvider({ children }: { children: React.ReactNode }) {
  const [opened, { close, open }] = useDisclosure(false);
  const [params, setParams] = useState<OpenPasswordValidityModalParams | undefined>(undefined);

  /**
   * Opens the password validity modal for the selected user.
   */
  const openPasswordValidityModal: OpenPasswordValidityModal = (nextParams) => {
    setParams(nextParams);
    open();
  };

  const value = useMemo(() => ({ openPasswordValidityModal }), [openPasswordValidityModal]);

  return (
    <PasswordValidityModalContext.Provider value={value}>
      {children}
      {opened && <PasswordValidityModal opened onClose={close} {...params!} />}
    </PasswordValidityModalContext.Provider>
  );
}

/**
 * Hook for opening the password validity modal.
 */
export function usePasswordValidityModal() {
  return useContext(PasswordValidityModalContext);
}
